import { useQueryClient } from '@tanstack/react-query';
import { CreateTrimbleLayerApiRequest } from '../../../../types/requests/trimble-layers/create-trimble-layer.request';
import { UpdateTrimbleLayerApiRequest } from '../../../../types/requests/trimble-layers/update-trimble-layer.request';
import { DeleteTrimbleLayerApiRequest } from '../../../../types/requests/trimble-layers/delete-trimble-layer.request';
import { GetTrimbleLayerApiRequest } from '../../../../types/requests/trimble-layers/get-trimble-layer.request';
import { SearchTrimbleLayersApiRequest } from '../../../../types/requests/trimble-layers/search-trimble-layers.request';
import { TrimbleLayerApiModel } from '../../../../types/models/trimble-layer.model';
import {
  createTrimbleLayer,
  deleteTrimbleLayer,
  getTrimbleLayer,
  searchTrimbleLayers,
  updateTrimbleLayer,
} from '../../api/trimble-layers.api';
import { useAxiosInfiniteQuery, useAxiosMutation, useAxiosQuery } from './use-axios.hook';
import { ImportTrimbleLayersApiRequest } from '../../../../types/requests/sync/import-trimble-layers.request';
import { exportTrimbleLayers, importTrimbleLayers } from '../../api/sync.api';

export const baseQueryKey = 'trimble-layer';
const searchQueryKey = 'search';

export const useCreateTrimbleLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: CreateTrimbleLayerApiRequest) => (await createTrimbleLayer(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, { ...result, trimbleLayer: data }] as const;
};

export const useUpdateTrimbleLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: UpdateTrimbleLayerApiRequest) => (await updateTrimbleLayer(params)).data,
    onSuccess: (trimbleLayer) =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) =>
          queryKey[0] === baseQueryKey &&
          (queryKey[1] === searchQueryKey || (queryKey[1] as GetTrimbleLayerApiRequest)?.trimbleLayerId === trimbleLayer.id),
      }),
  });
  return [request, { ...result, trimbleLayer: data }] as const;
};

export const useGetTrimbleLayer = <T extends TrimbleLayerApiModel>(params: GetTrimbleLayerApiRequest) => {
  const { data, ...result } = useAxiosQuery<T>({
    queryKey: [baseQueryKey, params],
    queryFn: async () => (await getTrimbleLayer<T>(params)).data,
    enabled: Boolean(params.trimbleLayerId && !Number.isNaN(params.trimbleLayerId)),
  });
  return { ...result, trimbleLayer: data } as const;
};

export const useSearchTrimbleLayers = <T extends TrimbleLayerApiModel>(params: SearchTrimbleLayersApiRequest) => {
  const { data, ...result } = useAxiosInfiniteQuery({
    queryKey: [baseQueryKey, searchQueryKey, params],
    queryFn: async ({ pageParam }) => (await searchTrimbleLayers<T>({ cursor: pageParam, ...params })).data,
    initialPageParam: params.cursor,
    getNextPageParam: (lastResponse) => lastResponse.nextCursor,
  });
  return { ...result, response: data } as const;
};

export const useDeleteTrimbleLayer = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: DeleteTrimbleLayerApiRequest) => (await deleteTrimbleLayer(params)).data,
    onSuccess: () =>
      queryClient.invalidateQueries({
        predicate: ({ queryKey }) => queryKey[0] === baseQueryKey && queryKey[1] === searchQueryKey,
      }),
  });
  return [request, result] as const;
};

export const useImportTrimbleLayers = () => {
  const queryClient = useQueryClient();
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async (params: ImportTrimbleLayersApiRequest) => (await importTrimbleLayers(params)).data,
    onSuccess: () => queryClient.invalidateQueries({ predicate: ({ queryKey }) => queryKey[0] === baseQueryKey }),
  });
  return [request, { response: data, ...result }] as const;
};

export const useExportTrimbleLayers = () => {
  const [request, { data, ...result }] = useAxiosMutation({
    mutationFn: async () => (await exportTrimbleLayers()).data,
  });
  return [request, result] as const;
};
